import React, { FC, useState, useEffect, Fragment } from 'react';
import { Box, InputAdornment, Typography, TextField, Tooltip } from '@mui/material';
import Button from 'components/Button/Button';
import { Info as InfoIcon, CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { useAppSelector } from 'app/hooks';
import { colorPalette } from 'styles/partials/colors';
import { useGetAllSharedCredentialsQuery, usePostCredentialMutation } from 'features/docuSign/docuSignApi';
import { isValidEmail } from 'utils/emailValidation';
import { AzulSkeleton } from '@the-ksquare-group/azul-ds';
import styles from './MyAccountSettings.module.css';

export interface MyAccountSettingsProps {
  text: string;
}

const MyAccountSettings: FC<MyAccountSettingsProps> = ({ text }) => {
  const useSelector = useAppSelector;
  const authUser = useSelector(state => state.auth.user);

  const [docuSignEmail, setDocuSignEmail] = useState<string | null>(null);
  const [inputEmail, setInputEmail] = useState<string>('');
  const { data, isLoading } = useGetAllSharedCredentialsQuery('');
  const [postCredendial, { isSuccess }] = usePostCredentialMutation();

  useEffect(() => {
    if (data && data.data) {
      const { data: sharedCredentials } = data;
      const [firstCredential] = sharedCredentials;
      if (firstCredential) {
        setDocuSignEmail(firstCredential.docuSignEmail);
      }
    }
  }, [data]);

  useEffect(() => {
    if (isSuccess) window.open(`${process.env.REACT_APP_BASE_URL_DEV}/docusignauth/login`, '_blank');
  }, [isSuccess]);

  const isEmailValid = (email: string) => isValidEmail(email);

  const LoginDocuSign = async () => {
    await postCredendial(inputEmail);
  };

  const handleOnChangeEmail = (email: string) => {
    setInputEmail(email);
  };

  function docuSignAccessComponent(isDocuSignConnected: boolean) {
    if (isDocuSignConnected) {
      return (
        <Box className={styles.sectionWrapper}>
          <Box className={styles.sectionTitle}>
            <Typography variant='h6' component='h3' fontSize='20px'>
              Great news!
            </Typography>
            <Tooltip
              title={
                <p>
                  Please be aware that if you <br /> log in and 30 days have <br /> passed, you&apos;ll need to log in{' '}
                  <br />
                  again.
                </p>
              }
              placement='top'
              arrow
            >
              <InfoIcon className={styles.infoIcon} />
            </Tooltip>
          </Box>
          <Box className={styles.sectionMessage}>
            <CheckCircleIcon className={styles.checkCircleIcon} />
            <p>You are now connected to DocuSign.</p>
          </Box>
          <Box>
            <TextField
              label='Email Address'
              type='email'
              className={styles.emailInput}
              sx={{ background: colorPalette.backgroundWhite }}
              disabled={true}
              value={docuSignEmail}
            />
          </Box>
        </Box>
      );
    } else {
      return (
        <Box className={styles.sectionWrapper}>
          <Box className={styles.sectionTitle}>
            <Typography variant='h6' component='h3' fontSize='20px'>
              Use Your DocuSign Account
            </Typography>
            <Tooltip
              title={
                <p>
                  Please be aware that if you <br /> log in and 30 days have <br /> passed, you&apos;ll need to log in{' '}
                  <br />
                  again.
                </p>
              }
              placement='top'
              arrow
            >
              <InfoIcon className={styles.infoIcon} />
            </Tooltip>
          </Box>
          <p>
            Connect your account through your email and start enjoying <br /> the benefits of DocuSign.
          </p>
          <Box className={styles.sectionActions}>
            <TextField
              className={styles.emailInput}
              sx={{ background: colorPalette.backgroundWhite }}
              label='Email Address'
              type='email'
              value={inputEmail}
              InputProps={{
                endAdornment: isEmailValid(inputEmail) && (
                  <InputAdornment position='end'>
                    <CheckCircleIcon />
                  </InputAdornment>
                ),
              }}
              onChange={e => handleOnChangeEmail(e.target.value)}
            />
            <Button disabled={!isEmailValid(inputEmail)} variant='secondary' onClick={LoginDocuSign}>
              Connect With DocuSign
            </Button>
          </Box>
        </Box>
      );
    }
  }

  return (
    <Box className={styles.myAccountSettingsPage}>
      <Typography variant='h5' component='h1' className={styles.pageTitle}>
        {text}
      </Typography>
      {authUser && (
        <Box className={styles.userInfoContainer}>
          <Box className={styles.initialCircle}>{authUser.name?.charAt(0)}</Box>
          <Box>
            <p className={styles.userName}>{authUser.name}</p>
            <p className={styles.userEmail}>{authUser.email}</p>
          </Box>
        </Box>
      )}
      <Box>
        <Typography variant='h6' component='h2' fontSize='32px' marginBottom='24px'>
          DocuSign Access
        </Typography>
        {isLoading ? (
          <AzulSkeleton width={654} height={201} />
        ) : (
          <Fragment>{docuSignAccessComponent(!!docuSignEmail)}</Fragment>
        )}
      </Box>
    </Box>
  );
};

export default MyAccountSettings;
